import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HelmetProvider } from 'react-helmet-async'
import './index.css'
import './i18n'
import App from './App.tsx'
import { AuthProvider } from './features/auth/AuthContext'
import { ColorSchemeProvider } from './features/landing/ColorSchemeContext'
import { ConfirmDialogProvider } from './components/ui/ConfirmDialog'
import { MessagesSocketProvider } from './features/messages/MessagesSocketContext'

// Le service worker PWA précache le shell JS/HTML : les utilisateurs qui l'ont installé
// se retrouvent bloqués sur un vieux shell référençant des chunks supprimés aux déploiements
// suivants (MIME text/html au lieu de JS). Le build ne génère plus de SW (voir vite.config.ts) ;
// on désinscrit ici tout SW restant chez les utilisateurs déjà piégés, puis on recharge une
// seule fois pour repartir sur une requête réseau propre.
const RELOAD_GUARD_KEY = 'sw-cleanup-reload-once'

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    if (registrations.length === 0) return
    Promise.all(registrations.map((registration) => registration.unregister())).then(() => {
      if ('caches' in window) {
        caches.keys().then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
      }
      if (!sessionStorage.getItem(RELOAD_GUARD_KEY)) {
        sessionStorage.setItem(RELOAD_GUARD_KEY, '1')
        window.location.reload()
      }
    })
  })
}

const queryClient = new QueryClient()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <MessagesSocketProvider>
              <ColorSchemeProvider>
                <ConfirmDialogProvider>
                  <App />
                </ConfirmDialogProvider>
              </ColorSchemeProvider>
            </MessagesSocketProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  </StrictMode>,
)
